import { ApolloError } from '@apollo/client';

/* 옵션 1회 구매수량 상한 정책 (product-api !219)
 * - 호스트는 1~10만 설정할 수 있고, 11~100은 CS(어드민)가 예외로 설정한다.
 * - 0(미설정)은 서버에서 100으로 저장된다.
 */
export const HOST_MAX_PURCHASABLE_COUNT = 10;
export const DEFAULT_PURCHASABLE_COUNT = 10;
export const SYSTEM_MAX_PURCHASABLE_COUNT = 100;

const PURCHASE_LIMIT_VALIDATION_ERROR_CODE = 'PD560001';
const PURCHASE_LIMIT_FIELD_NAME = 'maximumPurchasableCount';

/** 저장값이 0이거나 10을 넘으면 CS가 설정한 값으로 본다. 저장값이 없으면(새 옵션, 복제 옵션) 아니다. */
export const isAdminSetPurchasableCount = (savedCount?: number): boolean => {
  if (savedCount === undefined || savedCount === null) {
    return false;
  }

  return savedCount === 0 || savedCount > HOST_MAX_PURCHASABLE_COUNT;
};

/** 관리자 설정 값 표시용. 0은 100으로 저장되므로 100개로 보여준다. */
export const getAdminPurchasableCountLabel = (savedCount: number): number =>
  savedCount === 0 ? SYSTEM_MAX_PURCHASABLE_COUNT : savedCount;

/** 호스트가 보낼 수 있는 값인지 (1~10) */
export const isHostPurchasableCount = (count: number): boolean =>
  Number.isInteger(count) && count >= 1 && count <= HOST_MAX_PURCHASABLE_COUNT;

/** 복제한 옵션은 새 옵션으로 저장되므로 1~10 밖의 값(0 포함)은 기본값 10으로 바꾼다. */
export const normalizeCopiedPurchasableCount = (count: number): number =>
  isHostPurchasableCount(count) ? count : DEFAULT_PURCHASABLE_COUNT;

/**
 * 저장할 때 서버 검증을 통과하는 값인지.
 * 1~10이거나, 관리자 설정 옵션을 건드리지 않아 저장값과 같으면 통과한다.
 */
export const isValidPurchasableCountToSave = (
  count: number,
  savedCount?: number,
): boolean => {
  if (isHostPurchasableCount(count)) {
    return true;
  }

  return isAdminSetPurchasableCount(savedCount) && count === savedCount;
};

interface ValidationErrorEntry {
  code?: string;
  fieldName?: string;
  message?: string;
}

const collectValidationErrors = (error: unknown): ValidationErrorEntry[] => {
  if (!(error instanceof ApolloError)) {
    return [];
  }

  return error.graphQLErrors.flatMap(graphQLError => {
    const extensions = (graphQLError.extensions ?? {}) as any;
    const exception = extensions.exception ?? {};
    const code = extensions.code ?? exception.code;
    const nested = exception.errors ?? extensions.errors;

    if (Array.isArray(nested)) {
      return nested.map((item: ValidationErrorEntry) => ({
        code: item.code ?? code,
        fieldName: item.fieldName,
        message: item.message,
      }));
    }

    return [
      {
        code,
        fieldName: extensions.fieldName ?? exception.fieldName,
        message: graphQLError.message,
      },
    ];
  });
};

/** 1회 구매수량 상한 검증 에러(PD560001, maximumPurchasableCount)면 서버 문구를 모아 돌려준다. */
export const getPurchaseLimitErrorMessage = (error: unknown): string | null => {
  const messages = collectValidationErrors(error)
    .filter(
      item =>
        item.fieldName === PURCHASE_LIMIT_FIELD_NAME &&
        (item.code === undefined ||
          item.code === PURCHASE_LIMIT_VALIDATION_ERROR_CODE),
    )
    .map(item => item.message)
    .filter((message): message is string => !!message);

  if (messages.length === 0) {
    return null;
  }

  return messages.join('\n');
};

/** 1회 구매수량 상한 검증 에러면 서버 문구를 토스트로 띄우고 true를 돌려준다. */
export const handlePurchaseLimitError = (error: unknown): boolean => {
  const message = getPurchaseLimitErrorMessage(error);

  if (!message) {
    return false;
  }

  window.showToast(message, 'failed', 5000);

  return true;
};
